import React from "react";
import { Helmet } from "react-helmet-async";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/context/LanguageContext";

export default function FAQ({ variant = "home" }) {
  const { t, lang } = useLang();
  const isAR = lang === "ar";
  const key = variant === "market" ? "faq.market_items"
    : variant === "mortgage" ? "faq.mortgage_items"
    : variant === "insights" ? "faq.insights_items"
    : "faq.items";
  const items = t(key) || [];

  // Dynamic FAQPage JSON-LD, reflects the actually-rendered Q/A in the
  // active language. GEO engines (Perplexity, ChatGPT search, Claude) pick
  // up FAQPage schema to answer buyer questions with our brand cited.
  const faqSchema = items.length > 0 && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "inLanguage": isAR ? "ar" : "en",
    "mainEntity": items.map((it) => ({
      "@type": "Question",
      "name": it.q,
      "acceptedAnswer": { "@type": "Answer", "text": it.a }
    }))
  };

  return (
    <section id="faq" className="section relative">
      {faqSchema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>
      )}
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <p className="font-mono-brand text-propertic-gold mb-4">{isAR ? "الأسئلة الشائعة" : "FAQ"}</p>
        <h2 className="font-editorial text-2xl sm:text-3xl mb-10 text-propertic-ink leading-[1.15]">
          {isAR ? (
            <>الأسئلة<br /><span className="font-italic text-propertic-gold">الأكثر شيوعاً.</span></>
          ) : (
            <>Frequently<br /><span className="font-italic text-propertic-gold">asked.</span></>
          )}
        </h2>
        <Accordion type="single" collapsible className="w-full space-y-4" data-testid="faq-accordion">
          {items.map((it, idx) => (
            <AccordionItem key={idx} value={`item-${idx}`} className="card-soft rounded-[1.5rem] px-6 border-b">
              <AccordionTrigger data-testid={`faq-q-${idx}`} className="font-editorial text-xl text-start text-propertic-navy hover:text-propertic-gold py-6 hover:no-underline">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-propertic-ink-2/95 text-[15px] leading-[1.75] pb-6">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
